import BadgeCheckIcon from '@heroicons/react/outline/esm/BadgeCheckIcon.js'
import ChatAlt2Icon from '@heroicons/react/outline/esm/ChatAlt2Icon.js'
import IdentificationIcon from '@heroicons/react/outline/esm/IdentificationIcon.js'
import MenuAlt2Icon from '@heroicons/react/outline/esm/MenuAlt2Icon.js'
import PhotographIcon from '@heroicons/react/outline/esm/PhotographIcon.js'
import TagIcon from '@heroicons/react/outline/esm/TagIcon.js'
import UserCircleIcon from '@heroicons/react/outline/esm/UserCircleIcon.js'
import UsersIcon from '@heroicons/react/outline/esm/UsersIcon.js'

import { FilterAppearances } from '@tribeplatform/react-components/Filters'
import { I18nShape } from '@tribeplatform/react-components/i18n'

import { StaticFieldTypes } from '../Views/types.js'
import type { StaticField } from '../Views/types.js'
import { MembersBlockProps, MembersBlockView } from './types.js'

export const genericViewFactory: (intl: I18nShape) => MembersBlockView = ({
  $t,
}) => ({
  id: '',
  name: $t({
    defaultMessage: 'Default',
    id: 'Generics.Default',
  }),
  filterBy: [],
  inlineFilters: [],
  filterAppearance: FilterAppearances.INLINE,
  allowInlineFilters: false,
  sort: 'alphabetical',
  style: 'list',
  gallerySize: 'medium',
  showDivider: true,
  limit: 10,
  fields: [],
  showMore: false,
  spaceId: '{{ spaceId }}',
})

export const staticFieldsFactory: ({ $t }: I18nShape) => StaticField[] = ({
  $t,
}) => [
  {
    id: 'galleryBanner',
    label: $t({
      defaultMessage: '{MEMBER_CC} banner',
      id: 'MemberBlock.FieldLabels.MemberBanner',
    }),
    enabled: true,
    locked: false,
    styles: ['gallery', 'carousel'],
    type: StaticFieldTypes.banner,
    settings: {
      bannerStyle: 'banner',
      aspectRatio: 'aspect-[5/4]',
    },
    isStatic: true,
    icon: <PhotographIcon />,
  },
  {
    id: 'listAvatar',
    label: $t({
      defaultMessage: '{MEMBER_CC} avatar',
      id: 'MemberBlock.FieldLabels.MemberAvatar',
    }),
    enabled: true,
    locked: false,
    styles: ['list'],
    type: StaticFieldTypes.avatar,
    settings: {
      avatarSize: 'md',
    },
    isStatic: true,
    icon: <UserCircleIcon />,
  },
  {
    id: 'magazineAvatar',
    label: $t({
      defaultMessage: '{MEMBER_CC} avatar',
      id: 'MemberBlock.FieldLabels.MemberAvatar',
    }),
    enabled: true,
    locked: true,
    styles: ['magazine'],
    type: StaticFieldTypes.avatar,
    settings: {
      avatarSize: 'lg',
    },
    isStatic: true,
    icon: <UserCircleIcon />,
  },
  {
    id: 'listMemberInfo',
    label: $t({
      defaultMessage: '{MEMBER_CC} info',
      id: 'MemberBlock.FieldLabels.MemberInfo',
    }),
    enabled: true,
    locked: false,
    styles: ['list'],
    settings: {
      stretchX: true,
    },
    isStatic: true,
    icon: <IdentificationIcon />,
    fields: [
      {
        id: 'listMemberName',
        label: $t({
          defaultMessage: '{MEMBER_CC} name',
          id: 'MemberBlock.FieldLabels.MemberName',
        }),
        enabled: true,
        locked: false,
        type: StaticFieldTypes.title,
        settings: {
          titleSize: 'md',
        },
        isStatic: true,
        icon: <MenuAlt2Icon />,
      },
      {
        id: 'listTagline',
        label: $t({
          defaultMessage: 'Tagline',
          id: 'Generics.Tagline',
        }),
        enabled: true,
        locked: false,
        settings: {},
        isStatic: true,
        icon: <TagIcon />,
      },
      {
        id: 'listBadges',
        label: $t({
          defaultMessage: 'Badges',
          id: 'Generics.Badges',
        }),
        enabled: true,
        locked: false,
        settings: {},
        isStatic: true,
        icon: <BadgeCheckIcon />,
      },
    ],
  },
  {
    id: 'galleryMemberName',
    label: $t({
      defaultMessage: '{MEMBER_CC} name',
      id: 'MemberBlock.FieldLabels.MemberName',
    }),
    enabled: true,
    locked: false,
    styles: ['gallery', 'magazine', 'carousel'],
    type: StaticFieldTypes.title,
    settings: {
      fullWidth: true,
      titleSize: 'md',
    },
    isStatic: true,
    icon: <MenuAlt2Icon />,
  },
  {
    id: 'galleryTagline',
    label: $t({
      defaultMessage: 'Tagline',
      id: 'Generics.Tagline',
    }),
    enabled: true,
    locked: false,
    styles: ['gallery', 'magazine', 'carousel'],
    settings: {
      fullWidth: true,
    },
    isStatic: true,
    icon: <TagIcon />,
  },
  {
    id: 'galleryBadges',
    label: $t({
      defaultMessage: 'Badges',
      id: 'Generics.Badges',
    }),
    enabled: true,
    locked: false,
    styles: ['gallery', 'magazine', 'carousel'],
    settings: {
      fullWidth: true,
    },
    isStatic: true,
    icon: <BadgeCheckIcon />,
  },
  {
    id: 'listMessageButton',
    label: $t({
      defaultMessage: 'Message button',
      id: 'MemberBlock.FieldLabels.MessageButton',
    }),
    enabled: true,
    locked: true,
    type: StaticFieldTypes.button,
    styles: ['list', 'gallery', 'carousel'],
    settings: { buttonVariant: 'iconText' },
    isStatic: true,
    icon: <ChatAlt2Icon />,
  },
  {
    id: 'magazineMessageButton',
    label: $t({
      defaultMessage: 'Message button',
      id: 'MemberBlock.FieldLabels.MessageButton',
    }),
    enabled: true,
    locked: false,
    type: StaticFieldTypes.button,
    styles: ['magazine'],
    settings: { buttonVariant: 'iconText' },
    isStatic: true,
    icon: <ChatAlt2Icon />,
  },
]

export const MembersConfig: (intl: I18nShape) => {
  defaultProps: MembersBlockProps
  displayName: string
  removable: boolean
  Icon: JSX.Element
} = ({ $t }) => ({
  defaultProps: {
    views: [],
    title: '',
    description: '',
    linkText: '',
    linkUrl: '',
  },
  displayName: $t({
    defaultMessage: 'Members',
    id: 'Blocks.Members.DisplayName',
  }),
  removable: true,
  Icon: <UsersIcon />,
})
